import React from 'react';
import { Button, PieChart } from '@privemind/components';
import { AssetCard } from '../components/asset-card/asset-card';
import { formatCurrency, formatPnL, formatPercentage } from '@privemind/utils/formatting';
import { AssetDisplayData, ChartData } from '../types';
import './home-ui.scss';

export interface HomeUIProps {
  // Data props
  totalAssets: number;
  totalPortfolioValue: number;
  totalCostBasis: number;
  totalPnL: number;
  totalPnLPercentage: number;
  assetDisplayData: AssetDisplayData[];
  portfolioChartData: ChartData[];
  
  // Callback props
  onEditAssetsClick: () => void;
}

export const HomeUI: React.FC<HomeUIProps> = ({
  totalAssets,
  totalPortfolioValue,
  totalCostBasis,
  totalPnL,
  totalPnLPercentage,
  assetDisplayData,
  portfolioChartData,
  onEditAssetsClick,
}) => {

  const getTopPerformingAssets = (): AssetDisplayData[] => {
    return [...assetDisplayData]
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 3);
  };

  const getBottomPerformingAssets = (): AssetDisplayData[] => {
    return [...assetDisplayData]
      .sort((a, b) => a.pnl - b.pnl)
      .slice(0, 3);
  };

  if (totalAssets === 0) {
    return (
      <div className="home">
        <div className="home__empty">
          <div className="home__empty-icon">📊</div>
          <h2>Welcome to your Crypto Portfolio Tracker!</h2>
          <p>You haven't added any assets to your portfolio yet.</p>
          <p>Get started by creating buckets and adding your crypto assets to track their performance.</p>
          <Button onClick={onEditAssetsClick}>
            Edit Assets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      <div className="home__stats">
        <div className="home__stat-card">
          <div className="home__stat-value">
            {totalCostBasis > 0 ? formatCurrency(totalCostBasis) : 'N/A'}
          </div>
          <div className="home__stat-label">Cost</div>
        </div>
        
        <div className="home__stat-card">
          <div className="home__stat-value">{formatCurrency(totalPortfolioValue)}</div>
          <div className="home__stat-label">Current Value</div>
        </div>
        
        <div className="home__stat-card">
          <div className="home__stat-value">{totalAssets}</div>
          <div className="home__stat-label">Total Assets</div>
        </div>
        
        <div className="home__stat-card">
          <div className={`home__stat-value ${totalPnL >= 0 ? 'home__stat-value--positive' : 'home__stat-value--negative'}`}>
            {formatPnL(totalPnL)}
          </div>
          <div className="home__stat-label">Profit/Loss ($)</div>
        </div>
        
        <div className="home__stat-card">
          <div className={`home__stat-value ${totalPnLPercentage >= 0 ? 'home__stat-value--positive' : 'home__stat-value--negative'}`}>
            {formatPercentage(totalPnLPercentage)}
          </div>
          <div className="home__stat-label">Profit/Loss (%)</div>
        </div>
      </div>

      {totalAssets > 0 && (
        <div className="home__performers-section">
          <div className="home__performers-container">
            <div className="home__performers-group">
              <h3 className="home__performers-title">Top 3 Performers</h3>
              <div className="home__performers-cards">
                {getTopPerformingAssets().map((asset) => (
                  <AssetCard
                    key={`top-${asset.id}_${asset.bucket}`}
                    name={asset.name}
                    symbol={asset.symbol}
                    pnl={asset.pnl}
                  />
                ))}
              </div>
            </div>
            <div className="home__performers-group">
              <h3 className="home__performers-title">Bottom 3 Performers</h3>
              <div className="home__performers-cards">
                {getBottomPerformingAssets().map((asset) => (
                  <AssetCard
                    key={`bottom-${asset.id}_${asset.bucket}`}
                    name={asset.name}
                    symbol={asset.symbol}
                    pnl={asset.pnl}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="home__chart-section">
        <div className="home__chart-container">
          <PieChart
            data={portfolioChartData}
            height={600}
            legend={{ pos: 'bottom' }}
          />
        </div>
      </div>
    </div>
  );
};
